import React from 'react'
import { create } from 'zustand'
import { subscribeWithSelector } from 'zustand/middleware'

import type { CryptoCode, FiatCode } from '../../constants/currencyCodes'
import type { WalletCurrency } from '../../domain/wallet/walletApi'

export interface CovertCryptoDetails {
  fiatAmount: {
    amount: string
    currency: FiatCode
  }
  cryptoAmount: {
    amount: string
    currency: CryptoCode
  }
  currencyInfoInWallet: WalletCurrency
}

export interface ConversionProps {
  from: CovertCryptoDetails
  to: CovertCryptoDetails
}

export interface InputControllerProps {
  lastFocusedInput: 'debit' | 'credit'
  debitInputRef: React.RefObject<any> | null
  creditInputRef: React.RefObject<any> | null
}

export interface SendConvertOrSellPayload {
  fee_amount: string
  debit_amount: string
  debit_currency: string
  credit_amount: string
  credit_currency: string
  convert_type: string
}

export interface GetEstimateConvertOrSellFeePayload {
  credit_currency: string
  debit_amount: string
  debit_currency: string
}

interface ConversionStoreState {
  completeCurrencies: WalletCurrency[]
  conversion: ConversionProps
  isFiatMode: boolean
  inputController: InputControllerProps
  conversionPayload: SendConvertOrSellPayload
  lastFeePayload: GetEstimateConvertOrSellFeePayload
  hasErrorOnFeeCalculation: boolean
}

interface ConversionStoreActions {
  setConversion: (conversion: Partial<ConversionProps>) => void
  setIsFiatMode: (isFiatMode: boolean) => void
  setInputController: (inputController: Partial<InputControllerProps>) => void
  setConversionPayload: (
    conversionPayload: Partial<SendConvertOrSellPayload>,
  ) => void
  setLastFeePayload: (
    lastFeePayload: GetEstimateConvertOrSellFeePayload,
  ) => void
  setHasErrorOnFeeCalculation: (hasErrorOnFeeCalculation: boolean) => void
  resetConversion: () => void
}

type ConversionStore = ConversionStoreState & ConversionStoreActions

const MAX_FOR_ON_RAMP = {
  fiat_currency: 'USD',
  fiat_amount: '19999',
  formattedAmount: '$19,999.00',
}

const MIN_FOR_ON_RAMP = {
  fiat_currency: 'USD',
  fiat_amount: '11',
  formattedAmount: '$11.00',
}

const generateRandomId = () => Math.random().toString(36).substr(2, 9)

const initialState: ConversionStoreState = {
  completeCurrencies: [
    {
      id: generateRandomId(),
      name: 'Bitcoin',
      code: 'BTC',
      amount: 0,
      amountWithoutNumberConversion: '0',
      icon: 'https://storage.googleapis.com/kem-catalog-stage/currencies/btc.png',
      fiatAmount: 0,
      fiatCurrency: 'USD',
      percentageChange24h: 0,
      revenue24hFiatAmount: 0,
      label: '₿',
      pricePerUnit: 43000,
      ceilingValueForValidation: 300,
      maxForOnRamp: MAX_FOR_ON_RAMP,
      minForOnRamp: MIN_FOR_ON_RAMP,
      formattedFiatBalance: '$0',
      formattedAmountBalance: '0 BTC',
      formattedUSDBalance: '$0',
      formattedStablecoinBalance: '$0',
      usdBalance: 0,
      displayCurrencyCode: 'BTC',
      symbol: '₿',
      avatarBGRefColor: '#F7931A',
      availableOnRampPaymentMethods: ['card', 'apple-pay'],
    },
    {
      id: generateRandomId(),
      name: 'Ethereum',
      code: 'ETH',
      amount: 0,
      amountWithoutNumberConversion: '0',
      icon: 'https://storage.googleapis.com/kem-catalog-stage/currencies/eth.png',
      fiatAmount: 0,
      fiatCurrency: 'USD',
      percentageChange24h: 0,
      revenue24hFiatAmount: 0,
      label: 'Ξ',
      pricePerUnit: 2300,
      ceilingValueForValidation: 300,
      maxForOnRamp: MAX_FOR_ON_RAMP,
      minForOnRamp: MIN_FOR_ON_RAMP,
      formattedFiatBalance: '$0',
      formattedAmountBalance: '0 ETH',
      formattedUSDBalance: '$0',
      formattedStablecoinBalance: '$0',
      usdBalance: 0,
      displayCurrencyCode: 'ETH',
      symbol: 'Ξ',
      avatarBGRefColor: '#627EEA',
      availableOnRampPaymentMethods: ['card', 'apple-pay'],
    },
    {
      id: generateRandomId(),
      name: 'Tether USD',
      code: 'USDT',
      amount: 0,
      amountWithoutNumberConversion: '0',
      icon: 'https://storage.googleapis.com/kem-catalog-stage/currencies/usdt.png',
      fiatAmount: 0,
      fiatCurrency: 'USD',
      percentageChange24h: 0,
      revenue24hFiatAmount: 0,
      label: '$',
      pricePerUnit: 1,
      ceilingValueForValidation: 300,
      maxForOnRamp: MAX_FOR_ON_RAMP,
      minForOnRamp: MIN_FOR_ON_RAMP,
      formattedFiatBalance: '$0',
      formattedAmountBalance: '0 USDT',
      formattedUSDBalance: '$0',
      formattedStablecoinBalance: '$0',
      usdBalance: 0,
      displayCurrencyCode: 'USDT',
      symbol: '$',
      avatarBGRefColor: '#33CC67',
      availableOnRampPaymentMethods: ['card', 'apple-pay'],
    },
  ],
  conversion: {
    from: {
      fiatAmount: {
        amount: '0',
        currency: 'USD' as FiatCode,
      },
      cryptoAmount: {
        amount: '0',
        currency: 'USDT' as CryptoCode,
      },
      currencyInfoInWallet: {
        id: generateRandomId(),
        name: 'U.S. Dollar',
        code: 'USDT',
        amount: 0,
        amountWithoutNumberConversion: '0',
        icon: 'https://storage.googleapis.com/kem-catalog-stage/currencies/usdt.png',
        fiatAmount: 0,
        fiatCurrency: 'USD',
        percentageChange24h: 0,
        revenue24hFiatAmount: 0,
        label: '$',
        pricePerUnit: 1,
        ceilingValueForValidation: 300,
        maxForOnRamp: MAX_FOR_ON_RAMP,
        minForOnRamp: MIN_FOR_ON_RAMP,
        formattedFiatBalance: '$0',
        formattedAmountBalance: '0 USDT',
        formattedUSDBalance: '$0',
        formattedStablecoinBalance: '$0',
        usdBalance: 0,
        displayCurrencyCode: 'USDT',
        symbol: '$',
        avatarBGRefColor: '#33CC67',
        availableOnRampPaymentMethods: ['card', 'apple-pay'],
      },
    },
    to: {
      fiatAmount: {
        amount: '0',
        currency: 'USD' as FiatCode,
      },
      cryptoAmount: {
        amount: '0',
        currency: 'BTC' as CryptoCode,
      },
      currencyInfoInWallet: {
        id: generateRandomId(),
        name: 'Bitcoin',
        code: 'BTC',
        amount: 0,
        amountWithoutNumberConversion: '0',
        icon: 'https://storage.googleapis.com/kem-catalog-stage/currencies/btc.png',
        label: '₿',
        fiatAmount: 0,
        fiatCurrency: 'USD',
        percentageChange24h: 0.95,
        revenue24hFiatAmount: 80751615.98,
        pricePerUnit: 43000,
        ceilingValueForValidation: 0.001,
        availableOnRampPaymentMethods: ['card', 'apple-pay'],
        formattedFiatBalance: '$0',
        formattedAmountBalance: '₿0',
        formattedUSDBalance: '$0',
        formattedStablecoinBalance: '$0',
        usdBalance: 0,
        displayCurrencyCode: 'BTC',
        symbol: '₿',
        avatarBGRefColor: '#F7931A',
        maxForOnRamp: MAX_FOR_ON_RAMP,
        minForOnRamp: MIN_FOR_ON_RAMP,
      },
    },
  },
  isFiatMode: true,
  inputController: {
    lastFocusedInput: 'debit',
    debitInputRef: null,
    creditInputRef: null,
  },
  conversionPayload: {
    fee_amount: '',
    debit_amount: '0',
    debit_currency: 'USDT',
    credit_amount: '0',
    credit_currency: 'BTC',
    convert_type: 'convert',
  },
  lastFeePayload: {
    credit_currency: 'USDT',
    debit_amount: '0',
    debit_currency: 'BTC',
  },
  hasErrorOnFeeCalculation: false,
}

export const useConversionScopedStore = create<ConversionStore>()(
  subscribeWithSelector((set, get) => ({
    ...initialState,
    setConversion: (conversion) =>
      set((state) => {
        // Get current state
        const currentConversion = state.conversion

        // Create new state with deep merge
        const newConversion = {
          from: conversion.from
            ? {
                ...currentConversion.from,
                ...conversion.from,
                fiatAmount: {
                  ...currentConversion.from.fiatAmount,
                  ...(conversion.from.fiatAmount || {}),
                },
                cryptoAmount: {
                  ...currentConversion.from.cryptoAmount,
                  ...(conversion.from.cryptoAmount || {}),
                },
              }
            : currentConversion.from,
          to: conversion.to
            ? {
                ...currentConversion.to,
                ...conversion.to,
                fiatAmount: {
                  ...currentConversion.to.fiatAmount,
                  ...(conversion.to.fiatAmount || {}),
                },
                cryptoAmount: {
                  ...currentConversion.to.cryptoAmount,
                  ...(conversion.to.cryptoAmount || {}),
                },
              }
            : currentConversion.to,
        }

        return {
          conversion: newConversion,
        }
      }),
    setConversionPayload: (conversionPayload) =>
      set((state) => ({
        conversionPayload: { ...state.conversionPayload, ...conversionPayload },
      })),
    setIsFiatMode: (isFiatMode) => set({ isFiatMode }),
    setLastFeePayload: (lastFeePayload) => set({ lastFeePayload }),
    setHasErrorOnFeeCalculation: (hasErrorOnFeeCalculation) =>
      set({ hasErrorOnFeeCalculation }),
    resetConversion: () => set({ ...initialState }),
    setInputController: (inputController) =>
      set((state) => ({
        inputController: {
          ...state.inputController,
          ...inputController,
        },
      })),
  })),
)
